import { useCallback, useEffect, useState } from 'react';
import { formatDateInTz, useOrgTimezone } from '../../utils/timezone';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import {
  Globe2, Loader2, AlertCircle, Plus, Search, X, ChevronLeft,
  CheckCircle, Clock, XCircle, Ban,
} from 'lucide-react';
import breederService from '../../services/breeder';
import organizationService, { type OrgSearchResult } from '../../services/organization';
import type {
  RegionLicensorGrant, RegionLicensorState, CreateGrantRequest,
} from '../../types/breeder';

const REGION_RE = /^[A-Z]{2}-[A-Z0-9]{1,3}$/;

const STATE_BADGE: Record<RegionLicensorState, { label: string; cls: string; icon: React.ReactNode }> = {
  pending:  { label: 'Pending',  cls: 'bg-amber-100 text-amber-800',  icon: <Clock className="h-3 w-3" /> },
  approved: { label: 'Approved', cls: 'bg-success-100 text-success-800', icon: <CheckCircle className="h-3 w-3" /> },
  rejected: { label: 'Rejected', cls: 'bg-error-100 text-error-800',  icon: <XCircle className="h-3 w-3" /> },
  revoked:  { label: 'Revoked',  cls: 'bg-neutral-200 text-neutral-700', icon: <Ban className="h-3 w-3" /> },
};

type Direction = 'licensor' | 'breeder';

function BreederLicensors() {
  const orgTz = useOrgTimezone();
  const [direction, setDirection] = useState<Direction>('licensor');
  const [grants, setGrants] = useState<RegionLicensorGrant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showClaimForm, setShowClaimForm] = useState(false);
  const [actioning, setActioning] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await breederService.listGrants({ as: direction });
      if (res.success && res.data) setGrants(res.data);
      else setError(res.message || 'Failed to load grants');
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message
        || 'Failed to load grants';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [direction]);

  useEffect(() => { void load(); }, [load]);

  const handleAction = async (
    id: number,
    fn: (id: number) => Promise<unknown>
  ) => {
    setActioning(id);
    try {
      await fn(id);
      await load();
    } finally {
      setActioning(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Region licensors"
        subtitle="Claim region licensor scope for a breeder, or manage inbound claims."
        icon={<Globe2 className="h-6 w-6 text-white" />}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4 flex items-center gap-3">
          <Link to="/breeder">
            <Button variant="outline" size="sm"><ChevronLeft className="h-4 w-4 mr-1" /> Dashboard</Button>
          </Link>
          <Button variant="primary" size="sm" onClick={() => setShowClaimForm(s => !s)}>
            <Plus className="h-4 w-4 mr-1" /> {showClaimForm ? 'Cancel' : 'Claim region licensor'}
          </Button>
        </div>

        {showClaimForm && (
          <ClaimForm onCreated={() => { setShowClaimForm(false); void load(); }} />
        )}

        {/* Direction tabs */}
        <div className="flex border-b border-neutral-200 mb-4">
          <TabButton active={direction === 'licensor'} onClick={() => setDirection('licensor')}>
            As licensor (us → others' cultivars)
          </TabButton>
          <TabButton active={direction === 'breeder'} onClick={() => setDirection('breeder')}>
            As breeder (others → our cultivars)
          </TabButton>
        </div>

        {loading ? (
          <Card><CardContent className="p-12 text-center">
            <Loader2 className="h-8 w-8 text-primary-600 animate-spin mx-auto mb-3" />
            <p className="text-neutral-600">Loading…</p>
          </CardContent></Card>
        ) : error ? (
          <Card><CardContent className="p-12 text-center">
            <AlertCircle className="h-10 w-10 text-error-600 mx-auto mb-3" />
            <p className="text-error-700">{error}</p>
          </CardContent></Card>
        ) : grants.length === 0 ? (
          <Card><CardContent className="p-12 text-center">
            <p className="text-neutral-500">
              {direction === 'licensor'
                ? 'You haven\'t claimed any region licensor grants yet.'
                : 'No-one has claimed to be your region licensor yet.'}
            </p>
          </CardContent></Card>
        ) : (
          <Card><CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-neutral-500 border-b border-neutral-200">
                  <th className="py-2 px-4 font-medium">{direction === 'licensor' ? 'Breeder' : 'Licensor'}</th>
                  <th className="py-2 px-4 font-medium">Region</th>
                  <th className="py-2 px-4 font-medium">Scope</th>
                  <th className="py-2 px-4 font-medium">State</th>
                  <th className="py-2 px-4 font-medium">Claimed</th>
                  <th className="py-2 px-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {grants.map(g => {
                  const badge = STATE_BADGE[g.state];
                  const counterparty = direction === 'licensor' ? g.breeder_org_name : g.licensor_org_name;
                  const showApprove = direction === 'breeder' && g.state === 'pending';
                  const showRevoke = g.state === 'pending' || g.state === 'approved';
                  return (
                    <tr key={g.id} className="border-b border-neutral-100">
                      <td className="py-2 px-4 font-medium text-neutral-900">
                        {counterparty}
                        {direction === 'licensor' && g.breeder_claim_state === 'unclaimed' && (
                          <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Placeholder</span>
                        )}
                      </td>
                      <td className="py-2 px-4 font-mono">{g.region_code}</td>
                      <td className="py-2 px-4 text-neutral-600">{g.scope.replace('_', ' ')}</td>
                      <td className="py-2 px-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${badge.cls}`}>
                          {badge.icon}{badge.label}
                        </span>
                      </td>
                      <td className="py-2 px-4 text-neutral-600 text-xs">
                        {formatDateInTz(g.claimed_at, orgTz)}
                      </td>
                      <td className="py-2 px-4 text-right">
                        <div className="flex justify-end gap-2">
                          {showApprove && (
                            <>
                              <Button variant="outline" size="sm" disabled={actioning === g.id}
                                onClick={() => handleAction(g.id, breederService.rejectGrant.bind(breederService))}>
                                <X className="h-3.5 w-3.5 mr-1" /> Reject
                              </Button>
                              <Button variant="primary" size="sm" disabled={actioning === g.id}
                                onClick={() => handleAction(g.id, breederService.approveGrant.bind(breederService))}>
                                <CheckCircle className="h-3.5 w-3.5 mr-1" /> Approve
                              </Button>
                            </>
                          )}
                          {showRevoke && !showApprove && (
                            <Button variant="outline" size="sm" disabled={actioning === g.id}
                              onClick={() => handleAction(g.id, breederService.revokeGrant.bind(breederService))}>
                              <Ban className="h-3.5 w-3.5 mr-1" /> Revoke
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
        active ? 'border-primary-600 text-primary-700' : 'border-transparent text-neutral-500 hover:text-neutral-700'
      }`}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// ClaimForm: org self-claims as region licensor for a breeder.
// ---------------------------------------------------------------------------

function ClaimForm({ onCreated }: { onCreated: () => void }) {
  const [breederQuery, setBreederQuery] = useState('');
  const [breederResults, setBreederResults] = useState<OrgSearchResult[]>([]);
  const [selectedBreeder, setSelectedBreeder] = useState<OrgSearchResult | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);

  const [usePlaceholder, setUsePlaceholder] = useState(false);
  const [placeholderName, setPlaceholderName] = useState('');
  const [placeholderCountry, setPlaceholderCountry] = useState('');
  const [placeholderWebsite, setPlaceholderWebsite] = useState('');

  const [regionCode, setRegionCode] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSearch = async (q: string) => {
    if (q.length < 2) { setBreederResults([]); return; }
    setSearching(true);
    try {
      const res = await organizationService.searchOrganizations({ name: q });
      if (res.success && res.data) setBreederResults(res.data);
    } finally { setSearching(false); }
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    const code = regionCode.trim().toUpperCase();
    if (!REGION_RE.test(code)) {
      setSubmitError('Region code must be ISO 3166-2 (e.g. AU-VIC, US-CA)');
      return;
    }
    if (!usePlaceholder && !selectedBreeder) {
      setSubmitError('Pick a breeder org or create a placeholder');
      return;
    }
    if (usePlaceholder && !placeholderName.trim()) {
      setSubmitError('Placeholder breeder needs at least a name');
      return;
    }

    setSubmitting(true);
    try {
      const body: CreateGrantRequest = {
        region_code: code,
        scope: 'all_cultivars',
        notes: notes || null,
        breeder_initiated: false,
      };
      if (usePlaceholder) {
        body.breeder_org_id = null;
        body.placeholder_breeder = {
          name: placeholderName.trim(),
          country: placeholderCountry || null,
          website: placeholderWebsite || null,
        };
      } else if (selectedBreeder) {
        body.breeder_org_id = selectedBreeder.id;
      }
      const res = await breederService.createGrant(body);
      if (res.success) {
        onCreated();
      } else {
        setSubmitError(res.message || 'Failed to create grant');
      }
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message
        || 'Failed to create grant';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="mb-6">
      <CardContent className="p-6">
        <h2 className="text-lg font-semibold text-neutral-900 mb-2">Claim region licensor scope</h2>
        <p className="text-sm text-neutral-600 mb-4">
          Tell us which breeder and region you'll act as licensor for. If the breeder isn't on the platform yet, create a placeholder — we'll auto-approve and let the real breeder claim it back later.
        </p>

        {/* Breeder selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-neutral-700 mb-2">Breeder</label>
          <div className="flex gap-3 mb-2 text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={!usePlaceholder} onChange={() => setUsePlaceholder(false)} />
              On the platform
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={usePlaceholder} onChange={() => setUsePlaceholder(true)} />
              Not yet on the platform (placeholder)
            </label>
          </div>

          {!usePlaceholder ? (
            <div className="relative">
              {selectedBreeder ? (
                <div className="flex items-center justify-between bg-primary-50 border border-primary-200 rounded-md px-3 py-2">
                  <span className="font-medium text-primary-900">{selectedBreeder.name}</span>
                  <Button variant="outline" size="sm" onClick={() => { setSelectedBreeder(null); setBreederQuery(''); }}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Input
                      value={breederQuery}
                      onChange={e => {
                        setBreederQuery(e.target.value);
                        setShowDropdown(true);
                        void handleSearch(e.target.value);
                      }}
                      onFocus={() => setShowDropdown(true)}
                      onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                      placeholder="Search breeder org…"
                    />
                    <div className="absolute right-3 top-2.5">
                      {searching ? <Loader2 className="h-4 w-4 animate-spin text-neutral-400" /> : <Search className="h-4 w-4 text-neutral-400" />}
                    </div>
                  </div>
                  {showDropdown && breederResults.length > 0 && (
                    <ul className="absolute z-10 mt-1 w-full bg-white border border-neutral-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {breederResults.map(o => (
                        <li
                          key={o.id}
                          className="px-3 py-2 hover:bg-neutral-50 cursor-pointer text-sm"
                          onMouseDown={() => { setSelectedBreeder(o); setBreederQuery(''); setShowDropdown(false); }}
                        >
                          {o.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <Input label="Breeder name" value={placeholderName} onChange={e => setPlaceholderName(e.target.value)} placeholder="Zaiger Genetics" />
              <div className="grid grid-cols-2 gap-2">
                <Input label="Country" value={placeholderCountry} onChange={e => setPlaceholderCountry(e.target.value)} placeholder="United States" />
                <Input label="Website" value={placeholderWebsite} onChange={e => setPlaceholderWebsite(e.target.value)} placeholder="https://…" />
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <Input
            label="Region code (ISO 3166-2)"
            value={regionCode}
            onChange={e => setRegionCode(e.target.value.toUpperCase())}
            placeholder="AU-VIC"
          />
          <Input
            label="Notes (optional)"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="e.g. exclusive Australian distributor"
          />
        </div>

        {submitError && <p className="text-sm text-error-700 mb-3">{submitError}</p>}

        <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
          {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
          Submit claim
        </Button>
      </CardContent>
    </Card>
  );
}

export default BreederLicensors;
